import { useState } from 'react';

import { Loader2, Plus, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useConfigurations } from '@/hooks/useConfigurations';
import {
  useCreateAssignment,
  useDeleteAssignment,
  useScenarioAssignments,
  useUpdateAssignment,
} from '@/hooks/useScenarios';
import {
  SCENARIO_CHANNELS,
  scenarioChannelKey,
  scenarioChannelLabel,
} from '@/lib/api/scenarios.api';

import type { ScenarioChannel } from '@/lib/api/scenarios.api';

interface AssignDialogProps {
  scenarioId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const AssignDialog = ({ scenarioId, open, onOpenChange }: AssignDialogProps) => {
  const { data: configurations, isLoading } = useConfigurations(open);
  const createMutation = useCreateAssignment();
  const [configurationId, setConfigurationId] = useState('');
  const [channelKey, setChannelKey] = useState(scenarioChannelKey(SCENARIO_CHANNELS[0]));
  const [required, setRequired] = useState(false);

  const channel: ScenarioChannel | undefined = SCENARIO_CHANNELS.find(
    (c) => scenarioChannelKey(c) === channelKey,
  );

  const handleSubmit = async () => {
    if (!configurationId || !channel) return;
    try {
      await createMutation.mutateAsync({
        scenarioId,
        configurationId,
        transport: channel.transport,
        modality: channel.modality,
        required,
      });
      onOpenChange(false);
      setConfigurationId('');
      setRequired(false);
    } catch {
      // Error toast is raised by the hook.
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Assign scenario</DialogTitle>
          <DialogDescription>
            Pick the configuration and channel this scenario should run on.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1">
            <label className="text-xs font-medium">Configuration</label>
            <Select value={configurationId} onValueChange={setConfigurationId}>
              <SelectTrigger>
                <SelectValue placeholder={isLoading ? 'Loading…' : 'Select a configuration'} />
              </SelectTrigger>
              <SelectContent>
                {(configurations ?? []).map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium">Channel</label>
            <Select value={channelKey} onValueChange={setChannelKey}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SCENARIO_CHANNELS.map((c) => (
                  <SelectItem key={scenarioChannelKey(c)} value={scenarioChannelKey(c)}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={required} onCheckedChange={(v) => setRequired(v === true)} />
            Required (blocks promotion when it fails)
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!configurationId || createMutation.isPending}>
            {createMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Assign
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const AssignmentsCard = ({ scenarioId }: { scenarioId: string }) => {
  const [assignOpen, setAssignOpen] = useState(false);
  const { data: assignments, isLoading } = useScenarioAssignments({ scenario_id: scenarioId });
  const updateMutation = useUpdateAssignment();
  const deleteMutation = useDeleteAssignment();

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-4">
        <div>
          <CardTitle>Assignments</CardTitle>
          <CardDescription>
            Where this scenario runs. Required assignments gate promotion; disabled ones are skipped
            by suite runs.
          </CardDescription>
        </div>
        <Button size="sm" variant="outline" onClick={() => setAssignOpen(true)}>
          <Plus className="mr-1 h-3.5 w-3.5" /> Assign
        </Button>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
          </div>
        ) : !assignments?.length ? (
          <p className="text-muted-foreground py-6 text-center text-sm">
            Not assigned to any configuration yet.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Configuration</TableHead>
                <TableHead>Channel</TableHead>
                <TableHead>Required</TableHead>
                <TableHead>Enabled</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {assignments.map((a) => (
                <TableRow key={a.id}>
                  <TableCell className="font-medium">
                    <Link to={`/configurations/${a.configuration_id}`} className="hover:underline">
                      {a.configuration?.name ?? a.configuration_id}
                    </Link>
                  </TableCell>
                  <TableCell className="text-sm">{scenarioChannelLabel(a)}</TableCell>
                  <TableCell>
                    <Switch
                      checked={a.required}
                      aria-label="Required"
                      disabled={updateMutation.isPending}
                      onCheckedChange={(required) =>
                        updateMutation.mutate({ id: a.id, input: { required } })
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={a.enabled}
                      aria-label="Enabled"
                      disabled={updateMutation.isPending}
                      onCheckedChange={(enabled) =>
                        updateMutation.mutate({ id: a.id, input: { enabled } })
                      }
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label="Remove assignment"
                      disabled={deleteMutation.isPending}
                      onClick={() => deleteMutation.mutate(a.id)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <AssignDialog scenarioId={scenarioId} open={assignOpen} onOpenChange={setAssignOpen} />
    </Card>
  );
};
